export const mapPaymentsForDisplay = (payments) =>
    payments.map((payment, index) => ({
        id: String(index),
        invoice: payment.invoiceNumber,
        invoiceValue: payment.invoiceValue,
        amount: payment.netValue,
        discount: payment.discountResult,
        finalAmount: payment.netValueWithDiscount,
        date: payment.invoiceDate,
        miscInfo: payment.miscInfo,
        transactionId: payment.transactionId,
    }));

export const sumAppliedAmounts = (appliedAmounts) =>
    Object.values(appliedAmounts).reduce(
        (accumulator, value) => accumulator + value,
        0
    );

export const buildAutomaticPartialStateForTypeA = (payments, partialPaymentAmount) => {
    let remaining = parseFloat(partialPaymentAmount) || 0;
    const deductibles = {};
    const appliedAmounts = {};
    const selectedPayments = [];

    for (const pay of payments) {
        const { id: paymentId, amount, finalAmount } = pay;

        if (remaining <= 0) {
            appliedAmounts[paymentId] = 0;
            deductibles[paymentId] = amount;
            continue;
        }

        if (remaining >= finalAmount) {
            appliedAmounts[paymentId] = finalAmount;
            deductibles[paymentId] = 0;
            selectedPayments.push(paymentId);
            remaining -= finalAmount;
            continue;
        }

        const coverage = Math.min(remaining, amount);
        appliedAmounts[paymentId] = coverage;
        deductibles[paymentId] = amount - coverage;

        if (coverage > 0) {
            selectedPayments.push(paymentId);
        }

        remaining -= coverage;
    }

    return {
        appliedAmounts,
        deductibles,
        selectedPayments,
    };
};

export const rebalancePartialStateForTypeMAmount = ({
    selectedPayments,
    payments,
    appliedAmounts,
    deductibles,
    totalAbono,
}) => {
    const nextAppliedAmounts = { ...appliedAmounts };
    const nextDeductibles = { ...deductibles };
    let totalApplied = sumAppliedAmounts(nextAppliedAmounts);

    if (totalApplied <= totalAbono) {
        return {
            appliedAmounts: nextAppliedAmounts,
            deductibles: nextDeductibles,
        };
    }

    let toRemove = totalApplied - totalAbono;

    for (let index = selectedPayments.length - 1; index >= 0 && toRemove > 0; index -= 1) {
        const paymentId = selectedPayments[index];
        const oldCoverage = nextAppliedAmounts[paymentId] || 0;

        if (oldCoverage <= 0) {
            continue;
        }

        const payment = payments.find((item) => item.id === paymentId);
        if (!payment) {
            continue;
        }

        if (oldCoverage <= toRemove) {
            nextAppliedAmounts[paymentId] = 0;
            nextDeductibles[paymentId] = payment.amount;
            toRemove -= oldCoverage;
            continue;
        }

        nextAppliedAmounts[paymentId] = oldCoverage - toRemove;
        nextDeductibles[paymentId] = payment.amount - nextAppliedAmounts[paymentId];
        toRemove = 0;
    }

    return {
        appliedAmounts: nextAppliedAmounts,
        deductibles: nextDeductibles,
    };
};

export const syncPartialSelectionForTypeM = ({
    selectedPayments,
    payments,
    appliedAmounts,
    deductibles,
    totalAbono,
}) => {
    const nextAppliedAmounts = { ...appliedAmounts };
    const nextDeductibles = { ...deductibles };
    let totalApplied = sumAppliedAmounts(nextAppliedAmounts);

    payments.forEach((payment) => {
        if (selectedPayments.includes(payment.id)) {
            return;
        }

        if (nextAppliedAmounts[payment.id] > 0) {
            totalApplied -= nextAppliedAmounts[payment.id];
        }

        nextAppliedAmounts[payment.id] = 0;
        nextDeductibles[payment.id] = payment.amount;
    });

    for (let index = 0; index < selectedPayments.length; index += 1) {
        const paymentId = selectedPayments[index];
        const payment = payments.find((item) => item.id === paymentId);

        if (!payment) {
            continue;
        }

        if (nextAppliedAmounts[paymentId] === undefined) {
            nextAppliedAmounts[paymentId] = 0;
            nextDeductibles[paymentId] = payment.amount;
        }

        const leftover = totalAbono - totalApplied;
        if (leftover <= 0) {
            continue;
        }

        const needed = payment.finalAmount - nextAppliedAmounts[paymentId];
        if (needed <= 0) {
            continue;
        }

        const coverage = Math.min(leftover, needed);
        nextAppliedAmounts[paymentId] += coverage;
        totalApplied += coverage;

        if (nextAppliedAmounts[paymentId] >= payment.finalAmount) {
            nextDeductibles[paymentId] = 0;
        } else {
            nextDeductibles[paymentId] = payment.amount - nextAppliedAmounts[paymentId];
        }
    }

    return {
        appliedAmounts: nextAppliedAmounts,
        deductibles: nextDeductibles,
    };
};

export const buildExternalReference = ({
    customerId,
    selectedInvoiceIds,
    payments,
    paymentOption,
    appliedAmounts,
    abonoAplicado,
    externalParams,
}) => {
    const currentDateTime = new Date();
    const pad = (value) => String(value).padStart(2, '0');
    const dateFormatted = `${pad(currentDateTime.getDate())}-${pad(currentDateTime.getMonth() + 1)}-${currentDateTime.getFullYear()}`;
    const timeFormatted = `${pad(currentDateTime.getHours())}-${pad(currentDateTime.getMinutes())}`;
    const abonoSuffix = `-ABONO-${dateFormatted}_${timeFormatted}`;

    const invoicesList =
        selectedInvoiceIds.length === 1
            ? (() => {
                const paymentId = selectedInvoiceIds[0];
                const payment = payments.find((item) => item.id === paymentId);
                if (!payment) {
                    return '';
                }

                const amountPaid =
                    paymentOption === 'complete'
                        ? payment.finalAmount
                        : abonoAplicado;

                const discountValue =
                    paymentOption === 'complete'
                        ? payment.discount
                        : amountPaid >= payment.finalAmount
                            ? payment.discount
                            : 0;

                const baseInvoice = `${payment.invoice}_${amountPaid}_DP-${discountValue}`;
                return paymentOption === 'partial'
                    ? baseInvoice + abonoSuffix
                    : baseInvoice;
            })()
            : selectedInvoiceIds
                .map((paymentId) => {
                    const payment = payments.find((item) => item.id === paymentId);
                    if (!payment) {
                        return null;
                    }

                    const amountPaid =
                        paymentOption === 'complete'
                            ? payment.finalAmount
                            : appliedAmounts[paymentId] || 0;

                    const discountValue =
                        paymentOption === 'complete'
                            ? payment.discount
                            : amountPaid >= payment.finalAmount
                                ? payment.discount
                                : 0;

                    const baseInvoice = `${payment.invoice}_${amountPaid}_DP-${discountValue}`;
                    return paymentOption === 'partial'
                        ? baseInvoice + abonoSuffix
                        : baseInvoice;
                })
                .filter((value) => value !== null)
                .join('-');

    let uniqueReference = `DOC-${customerId}-FAV-${invoicesList}`;
    const { cmpy, referencia, reg, teri, phone, evt, cash_received } = externalParams;

    if (cmpy && referencia) {
        uniqueReference = `${uniqueReference}_APP_EVA_CMPY_${cmpy}_REG_${reg}_TERI_${teri}_PHONE_${phone}_REF_${referencia}_EVT_${evt}_CASHRECEIVED_${cash_received}`;
    }

    return uniqueReference;
};
